import React, { useState, useEffect, useCallback } from 'react';
import './registers.css'
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatInTimeZone } from 'date-fns-tz';
import { Connection, Grid, Reservation, Status, Vessels } from '../../interfaces/interfaces';
import { deleteReservation, getConnection, getGridPointsAll, getReservation, getStatus, getVessel, patchConnection, patchReservation, postConnection, postReservation } from '../../services/requests';

const Registers: React.FC = () => {
    const role = Cookies.get('role');
    const nempresa = Cookies.get('Nempresa');
    const nembarcacion = Cookies.get('Nembarcacion');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([{ ID: "", Vessel: "", GridPoint: "", StartTime: "", Status: "", Status2: 0 }]);
    const [dataConnection, setDataConnection] = useState<Connection[]>([]);
    const [filteredDataConnection, setFilteredDataConnection] = useState(dataConnection);
    const [filteredDataConnectionID, setFilteredDataConnectionID] = useState(dataConnection);
    const [dataVessel, setDataVessel] = useState<Vessels[]>([]);
    const [filteredDataVessel, setFilteredDataVessel] = useState(dataVessel);
    const [dataGridPointAll, setDataGridPointAll] = useState<Grid[]>([]);
    const [dataGridPoint, setDataGridPoint] = useState<Grid[]>([]);
    const [filteredDataGridPoint, setFilteredDataGridPoint] = useState(dataGridPointAll);
    const [dataStatusConnection, setDataStatusConnection] = useState<Status[]>([]);
    const [dataStatusReservation, setDataStatusReservation] = useState<Status[]>([]);
    const ability = useAbility();
    const [minDate, setMinDate] = useState("");
    const { t } = useTranslation();

    /**
     * Formatea una fecha en formato ISO a un formato de fecha más legible (YYYY-MM-DD HH:MM:SS).
     * @param isoString - Cadena de texto que representa una fecha en formato ISO.
     * @returns Una cadena con la fecha y hora formateada.
     */
    const formatDate = (isoString: string): string => {
        const timeZone = 'Europe/Madrid';
        return formatInTimeZone(isoString, timeZone, 'yyyy-MM-dd HH:mm:ss');
    };

    /**
     * Obtiene la información de varias API y la organiza para su uso en la interfaz de usuario.
     * - Realiza varias solicitudes HTTP para obtener datos relacionados con reservas, conexiones, embarcaciones, etc.
     * - Filtra y transforma los datos para ser utilizados en la interfaz.
     * - Actualiza los estados con los datos obtenidos y procesados.
     */
    const getInformation = useCallback(async () => {
        try {

            const responseReservation = await getReservation();

            const responseConnection = await getConnection();

            const responseVessel = await getVessel();

            const responseStatus = await getStatus();

            const responseGridPointAll = await getGridPointsAll();

            setDataGridPointAll(responseGridPointAll.data.data as Grid[]);

            const filteredStatusConnection = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Cx" && (item.Nestado === 10 || item.Nestado === 9));
            const filteredStatusReservation = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Rs");

            setDataConnection(responseConnection.data.data as Connection[]);
            setFilteredDataConnection(responseConnection.data.data as Connection[]);
            setFilteredDataConnectionID(responseConnection.data.data as Connection[]);

            const filteredVessel = (responseVessel.data.data as Vessels[]).filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
            setDataVessel(filteredVessel);
            setFilteredDataVessel(filteredVessel);

            setDataGridPoint(responseGridPointAll.data.data as Grid[]);

            const filteredGridPointAll = (responseGridPointAll.data.data as Grid[]).filter(item => item.Status === 1);
            setFilteredDataGridPoint(filteredGridPointAll);
            setDataStatusConnection(filteredStatusConnection);
            setDataStatusReservation(filteredStatusReservation);

            const filteredReservation = (responseReservation.data.data as Reservation[]).filter(reservation => nembarcacion === '' || reservation.Nembarcacion === Number(nembarcacion))

            const validNembarcaciones = filteredVessel.map(vessel => vessel.Nembarcacion);

            const transformedData = filteredReservation
                .filter(reservation => validNembarcaciones.includes(reservation.Nembarcacion))
                .filter(reservation => reservation.Nreserva !== 0)
                .map(reservation => {
                    const vesselMatch = (responseVessel.data.data as Vessels[]).find(item => item.Nembarcacion === reservation.Nembarcacion);
                    const gridPointMatch = (responseGridPointAll.data.data as Grid[]).find(item => item.Nmonaguillo === reservation.Nmonaguillo);
                    const statusMatch = (responseStatus.data.data as Status[]).find(item => item.Nestado === reservation.Status);

                    return {
                        ID: reservation.Nreserva.toString(),
                        Vessel: vesselMatch ? vesselMatch.Tembarcacion.toString() : "",
                        GridPoint: gridPointMatch ? gridPointMatch.Tmonaguillo.toString() : "",
                        StartTime: formatDate(reservation.Festimada.toString() || ""),
                        Status: statusMatch ? statusMatch.Testado.toString() : "",
                        Status2: reservation.Status
                    };
                });

            setData(transformedData);
            setFilteredData(transformedData);
            setFilteredDataID(transformedData);

        } catch (error) {
            toast.error(`Error: ${error}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [nempresa, nembarcacion]);

    /**
     * Carga la información al montar el componente y establece fechas por defecto para los filtros de fecha.
     */
    useEffect(() => {
        getInformation();
        const nowUTC = new Date().toISOString().slice(0, 19);
        setMinDate(nowUTC);
        setModalDateInicial(nowUTC);
        setModalDateFinal(nowUTC);
    }, [getInformation]);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [filteredData, setFilteredData] = useState(data);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const totalPage = itemsPerPage === filteredData.length ? 1 : Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === filteredData.length ? filteredData.length : startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    /**
     * Avanza a la siguiente página en la paginación.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Regresa a la página anterior en la paginación.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Descarga los datos actuales en formato XLSX.
     */
    const downloadDataAsXLSX = () => {
        const sanitizedData = data.map(({ Status2, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(sanitizedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Registers Data');
        XLSX.writeFile(workbook, 'Registers Data.xlsx');
    };

    const [modalVesselNew, setModalVesselNew] = useState('');
    const [modalGridPointNew, setModalGridPointNew] = useState('');
    const [modalDateInicialNew, setModalDateInicialNew] = useState('');
    const [modalDateFinalNew, setModalDateFinalNew] = useState('');
    const [modalInitialConsumptionNew, setModalInitialConsumptionNew] = useState('');
    const [modalFinalConsumptionNew, setModalFinalConsumptionNew] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [modalVessel, setModalVessel] = useState('');
    const [modalGridPoint, setModalGridPoint] = useState('');
    const [modalDateInicial, setModalDateInicial] = useState('');
    const [modalDateFinal, setModalDateFinal] = useState('');
    const [modalInitialConsumption, setModalInitialConsumption] = useState('');
    const [modalFinalConsumption, setModalFinalConsumption] = useState('');
    const [modalStatus, setModalStatus] = useState('');

    /**
     * Muestra el modal para crear un nuevo reporte.
     */
    const handleCardClick = () => {
        setShowModal(true);
    };

    /**
     * Cierra el modal y reinicia todos los estados relacionados con el formulario de reservas.
     * - Oculta los modales de nuevo, edición y eliminación.
     * - Limpia los campos del formulario: embarcación, punto de conexión, fechas, consumo y estado.
     * - Restaura los campos de fecha a `minDate`.
     * - Restablece la vista principal a `"Reservations"`.
     */
    const closeModal = () => {
        setShowModal(false);
        setShowModalEdit(false);
        setShowModalDelete(false);
        setModalVessel("");
        setModalGridPoint("");
        setModalDateInicial("");
        setModalDateFinal("");
        setModalInitialConsumption("");
        setModalStatus("");
        setShowResolved('Reservations');
        setModalID("");
        setModalDateInicial(minDate);
        setModalDateFinal(minDate);
    };

    /**
     * Maneja el evento de clic en la superposición del modal.
     * Si el clic se realiza fuera del contenido del modal, cierra los modales y resetea los valores de los campos.
     * @param e - El evento de clic que se disparó al hacer clic en la superposición.
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            closeModal();
        }
    };

    /**
     * Obtiene el siguiente ID disponible para una nueva entrada, basado en los datos actuales.
     * @returns El siguiente ID como una cadena de texto.
     */
    const getNextId = (): string => {
        if (data.length === 0) return "1";

        const maxId = Math.max(...data.map(item => Number(item.ID)));
        return String(maxId + 1);
    };

    /**
     * Crea una nueva conexión con los datos ingresados en el formulario y realiza la solicitud para guardarla en el servidor.
     * Valida los campos requeridos y muestra mensajes de error si no se cumplen las condiciones.
     */
    const handleCreateConnection = async () => {

        if (!modalVesselNew || !modalGridPointNew || !modalDateInicialNew || !modalDateFinalNew || !modalInitialConsumptionNew || !modalFinalConsumptionNew) {
            toast.error(`${t('registers.pleaseCompleteAllFields')}`);
            return;
        }

        const finalValue = parseFloat(modalFinalConsumptionNew);
        const initialValue = parseFloat(modalInitialConsumptionNew);

        if (finalValue < initialValue) {
            toast.error(`${t('registers.finalConsumptionIsLowerThanInitialConsumption')}`);
            return;
        }

        try {
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVesselNew);
            const gridPointMatch = dataGridPoint.find(item => item.Tmonaguillo === modalGridPointNew);
            const statusMatch = dataStatusConnection.find(item => item.Nestado === 9);

            const body = {
                Nembarcacion: vesselMatch?.Nembarcacion,
                Nmonaguillo: gridPointMatch?.Nmonaguillo,
                Finicio: modalDateInicialNew,
                Ffinal: modalDateFinalNew,
                Cinicio: Number(modalInitialConsumptionNew),
                Cfinal: Number(modalFinalConsumptionNew),
                Status: statusMatch?.Nestado,
                Nconstante: 1,
            };

            const response = await postConnection(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${t('registers.addedConnectionWithID')}: ${getNextId()}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    /**
     * Crea una nueva reserva con los datos ingresados en el formulario y realiza la solicitud para guardarla en el servidor.
     * Valida los campos requeridos y asegura que no haya otra reserva asignada al mismo barco.
     * 
     * Si la reserva se realiza con éxito, recarga la página.
     */
    const handleCreateReservation = async () => {

        if (!modalVesselNew || !modalGridPointNew) {
            toast.error(`${t('registers.pleaseCompleteAllFields')}`);
            return;
        }

        const existsAssignedVessel = data.some(item =>
            item.Vessel === modalVesselNew && item.Status === 'Assigned'
        );

        if (existsAssignedVessel) {
            toast.error(`${t('registers.theShipAlreadyExistsWithAnAssignedReservation')}`);
            return;
        }

        try {
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVesselNew);
            const gridPointMatch = dataGridPointAll.find(item => item.Tmonaguillo === modalGridPointNew);

            let statusMatch;
            statusMatch = dataStatusReservation.find(item => item.Nestado === 16);
            const date = new Date().toISOString().slice(0, 19)

            const body = {
                Nembarcacion: vesselMatch?.Nembarcacion,
                Nmonaguillo: gridPointMatch?.Nmonaguillo,
                Festimada: date,
                Status: statusMatch?.Nestado,
            };

            const response = await postReservation(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${t('registers.reservationAddedWithID')}: ${getNextId()}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    const [isSelectOpenVessel, setIsSelectOpenVessel] = useState(false);
    const handleSelectFocusVessel = () => setIsSelectOpenVessel(true);
    const handleSelectBlurVessel = () => setIsSelectOpenVessel(false);

    const [isSelectOpenGridPoint, setIsSelectOpenGridPoint] = useState(false);
    const handleSelectFocusGridPoint = () => setIsSelectOpenGridPoint(true);
    const handleSelectBlurGridPoint = () => setIsSelectOpenGridPoint(false);

    const [isSelectOpenStatus, setIsSelectOpenStatus] = useState(false);
    const handleSelectFocusStatus = () => setIsSelectOpenStatus(true);
    const handleSelectBlurStatus = () => setIsSelectOpenStatus(false);

    const [isSelectOpenID, setIsSelectOpenID] = useState(false);
    const handleSelectFocusID = () => setIsSelectOpenID(true);
    const handleSelectBlurID = () => setIsSelectOpenID(false);

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof data[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "StartTime", direction: "desc" });

    /**
     * Efecto que se ejecuta cuando los datos cambian, ordenando los datos por el campo "StartTime" en orden descendente.
     */
    useEffect(() => {
        const sortedData = [...data].sort((a, b) => (a.StartTime < b.StartTime ? 1 : -1));
        setFilteredData(sortedData);
    }, [data]);

    /**
     * Ordena los datos según la clave proporcionada. Si los datos ya están ordenados por esa clave,
     * cambia la dirección de ordenamiento (ascendente a descendente y viceversa).
     * @param key - La clave del objeto por la cual se ordenarán los datos.
     */
    const sortData = (key: keyof typeof data[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sortedData = [...data].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setFilteredData(sortedData);
    };

    const [searchTerm, setSearchTerm] = useState('');

    /**
     * Filtra los datos según el término de búsqueda ingresado y actualiza los resultados.
     * Si se encuentra al menos un resultado, se establece la página actual en 1, de lo contrario en 0.
     */
    const handleSearch = () => {
        const filtered = data.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
        setFilteredData(filtered);

        if (filtered.length > 0) {
            setCurrentPage(1);
        } else {
            setCurrentPage(0);
        }
    };

    const [showResolved, setShowResolved] = useState('Reservations');

    const [showModalEdit, setShowModalEdit] = useState(false);
    const [modalID, setModalID] = useState('');
    const [modalIdTemp, setModalIdTemp] = useState('');
    const [modalStatusTemp, setModalStatusTemp] = useState(0);
    const [filteredDataID, setFilteredDataID] = useState(data);

    /**
     * Actualiza una conexión existente con los datos del modal. Si los campos son válidos,
     * se realiza la solicitud para actualizar la conexión en el servidor.
     */
    const handleUpdateConnection = async () => {

        if (!modalVessel || !modalGridPoint || !modalDateInicial || !modalDateFinal || !modalInitialConsumption || !modalFinalConsumption || !modalStatus) {
            toast.error(`${t('registers.pleaseCompleteAllFields')}`);
            return;
        }

        try {
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVessel);
            const gridPointMatch = dataGridPoint.find(item => item.Tmonaguillo === modalGridPoint);
            const statusMatch = dataStatusConnection.find(item => item.Testado === modalStatus);

            const body = {
                id: modalID,
                Nembarcacion: vesselMatch?.Nembarcacion,
                Nmonaguillo: gridPointMatch?.Nmonaguillo,
                Finicio: modalDateInicial,
                Ffinal: modalDateFinal,
                Cinicio: Number(modalInitialConsumption),
                Cfinal: Number(modalFinalConsumption),
                Status: statusMatch?.Nestado,
                Nconstante: 1,
            };

            const response = await patchConnection(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${t('registers.theConnectionWithIDWasUpdated')}: ${modalID}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    /**
     * Actualiza una reserva existente con los datos del modal. Si los campos son válidos,
     * se realiza la solicitud para actualizar la reserva en el servidor.
     * Recarga la página después de realizar la actualización.
     */
    const handleUpdateReservation = async () => {

        if (!modalVessel || !modalGridPoint || !modalDateInicial || !modalStatus) {
            toast.error(`${t('registers.pleaseCompleteAllFields')}`);
            return;
        }

        try {
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVessel);
            const gridPointMatch = dataGridPoint.find(item => item.Tmonaguillo === modalGridPoint);
            const statusMatch = dataStatusReservation.find(item => item.Testado === modalStatus);

            const body = {
                id: modalID,
                Nembarcacion: vesselMatch?.Nembarcacion,
                Nmonaguillo: gridPointMatch?.Nmonaguillo,
                Festimada: modalDateInicial,
                Status: statusMatch?.Nestado,
            };

            const response = await patchReservation(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${t('registers.theReservationWasUpdatedWithID')}: ${modalID}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    const [showModalDelete, setShowModalDelete] = useState(false);

    /**
     * Maneja la eliminación de una reserva a partir del `modalID`.
     * - Llama a la API `deleteReservation` con el ID de la reserva.
     * - Si la respuesta es exitosa (código 2xx), muestra un mensaje de éxito, 
     *   recarga la información con `getInformation()` y cierra el modal.
     * - Si la respuesta no es exitosa, muestra un mensaje de error con el código HTTP.
     * - Si ocurre un error (Axios o general), extrae y muestra el mensaje de error más informativo posible.
     */
    const handleDeleteReservation = async () => {

        try {

            const data = {
                id: modalID,
            };

            const response = await deleteReservation(data);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${t('registers.theReservationWasDeleteWithID')}: ${modalID}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    /**
     * Establece automáticamente la embarcación (`Tembarcacion`) en el modal si:
     * - El rol del usuario es "costumer".
     * - Solo hay una embarcación disponible en `filteredDataVessel`.
     * 
     * Esto facilita la selección predeterminada para usuarios con acceso limitado.
     */
    useEffect(() => {
        if (role === "costumer" && filteredDataVessel.length === 1) {
            setModalVessel(String(filteredDataVessel[0].Tembarcacion));
        }
    }, [role, filteredDataVessel]);

    return (
        <div className='containerConnections'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('registers.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('registers.registersToGrid')}</h4>
                        <div>
                            <button className="btn btn-dark" onClick={handleCardClick}>
                                <i className="fas fa-plus"></i> {t('registers.newReservation')}
                                {/* {(role === 'costumer' || role === 'manager') && (
                                    <>
                                        <i className="fas fa-plus"></i> {t('registers.newReservation')}
                                    </>
                                )} */}
                                {/* {role === 'admin' && (
                                    <>
                                        <i className="fas fa-plus"></i> {t('registers.newConnection')}
                                    </>
                                )} */}
                            </button>
                        </div>
                    </div>
                    <div className={`separator ${isFullScreen ? 'expanded' : ''}`}>
                        <div className='caption'>
                            <div className='left'>
                                <h5>{t('registers.registersList')}</h5>
                            </div>
                            <div className='right'>
                                <button className="btn search-btn" onClick={handleSearch}>
                                    <i className="fas fa-search"></i>
                                </button>

                                <input
                                    type="text"
                                    placeholder={t('registers.searchRegisters') + " ..."}
                                    className="form-control search-input"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                />

                                <button className="btn btn-dark download-btn" onClick={downloadDataAsXLSX}>
                                    <i className="fas fa-download"></i>
                                </button>

                                <button className="btn btn-dark fullscreen-btn" onClick={() => {
                                    setIsFullScreen(!isFullScreen);
                                }}>
                                    <i className={`fas ${isFullScreen ? 'fa-compress' : 'fa-expand'}`}></i>
                                </button>
                            </div>
                        </div>
                        <div className='tableConnections'>
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th onClick={() => sortData("ID")}>
                                            ID {sortConfig.key === "ID" ?
                                                (sortConfig.direction === "asc" ?
                                                    (<i className="fas fa-arrow-up" />) :
                                                    (<i className="fas fa-arrow-down" />)
                                                ) : (
                                                    <i className="fa-solid fa-arrow-down-a-z"></i>
                                                )}
                                        </th>
                                        <th onClick={() => sortData("Vessel")}>
                                            {t('registers.vessel')} {sortConfig.key === "Vessel" ?
                                                (sortConfig.direction === "asc" ?
                                                    (<i className="fas fa-arrow-up" />) :
                                                    (<i className="fas fa-arrow-down" />)
                                                ) : (
                                                    <i className="fa-solid fa-arrow-down-a-z"></i>
                                                )}
                                        </th>
                                        <th onClick={() => sortData("GridPoint")}>
                                            {t('registers.gridPoint')} {sortConfig.key === "GridPoint" ?
                                                (sortConfig.direction === "asc" ?
                                                    (<i className="fas fa-arrow-up" />) :
                                                    (<i className="fas fa-arrow-down" />)
                                                ) : (
                                                    <i className="fa-solid fa-arrow-down-a-z"></i>
                                                )}
                                        </th>
                                        <th onClick={() => sortData("StartTime")}>
                                            {t('registers.startTime')} {sortConfig.key === "StartTime" ?
                                                (sortConfig.direction === "asc" ?
                                                    (<i className="fas fa-arrow-up" />) :
                                                    (<i className="fas fa-arrow-down" />)
                                                ) : (
                                                    <i className="fa-solid fa-arrow-down-a-z"></i>
                                                )}
                                        </th>
                                        <th onClick={() => sortData("Status")}>
                                            {t('registers.status')} {sortConfig.key === "Status" ?
                                                (sortConfig.direction === "asc" ?
                                                    (<i className="fas fa-arrow-up" />) :
                                                    (<i className="fas fa-arrow-down" />)
                                                ) : (
                                                    <i className="fa-solid fa-arrow-down-a-z"></i>
                                                )}
                                        </th>
                                        <th>
                                            {t('registers.actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData.length > 0 && currentData[0]?.ID ? (
                                        currentData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.ID}</td>
                                                <td>{row.Vessel}</td>
                                                <td>{row.GridPoint}</td>
                                                <td>{row.StartTime}</td>
                                                <td>
                                                    <span className={`status status-${row.Status2}`}>
                                                        {row.Status}
                                                    </span>
                                                </td>
                                                {row.Status2 === 14 || row.Status2 === 16 ? (
                                                    <td className='actions'>
                                                        <i onClick={() => {
                                                            setShowModalEdit(true);
                                                            setModalID(row.ID);
                                                            const selectedItem = filteredData.find((data) => data.ID === row.ID);
                                                            const statusMatch = dataStatusReservation.find(data => data.Nestado === row.Status2);
                                                            setModalVessel(selectedItem ? selectedItem.Vessel : "");
                                                            setModalGridPoint(selectedItem ? selectedItem.GridPoint : "");
                                                            setModalDateInicial(selectedItem ? selectedItem.StartTime : "");
                                                            setModalStatus(statusMatch ? statusMatch.Testado.toString() : "");
                                                            setModalIdTemp(row.ID);
                                                            setModalStatusTemp(row.Status2);
                                                        }}
                                                            className="fas fa-edit"></i>

                                                        <i onClick={() => {
                                                            setShowModalDelete(true);
                                                            setModalID(row.ID);
                                                            const selectedItem = filteredData.find((data) => data.ID === row.ID);
                                                            const statusMatch = dataStatusReservation.find(data => data.Nestado === row.Status2);
                                                            setModalVessel(selectedItem ? selectedItem.Vessel : "");
                                                            setModalGridPoint(selectedItem ? selectedItem.GridPoint : "");
                                                            setModalDateInicial(selectedItem ? selectedItem.StartTime : "");
                                                            setModalStatus(statusMatch ? statusMatch.Testado.toString() : "");
                                                            setModalIdTemp(row.ID);
                                                            setModalStatusTemp(row.Status2);
                                                        }}
                                                            className="fas fa-trash"></i>
                                                    </td>
                                                ) : (
                                                    <td></td>
                                                )}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: "center" }}>{t('registers.noInformationAvailable')}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="pagination">
                            <button className="btn btn-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                                <i className="fas fa-chevron-left"></i> {t('registers.previous')}
                            </button>
                            <div>
                                <span>{t('registers.page')} {currentPage} {t('registers.of')} {totalPage},</span>
                                <div className="pagination-controls">
                                    <span>{t('registers.rowsPerPage')}:</span>
                                    <select
                                        className="form-control rows-select"
                                        value={
                                            itemsPerPage === filteredData.length && ![5, 10, 15, 50].includes(itemsPerPage)
                                                ? "All"
                                                : itemsPerPage
                                        }
                                        onChange={(e) => {
                                            const value = e.target.value === "All" ? filteredData.length : parseInt(e.target.value, 10);
                                            setItemsPerPage(value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="5">5</option>
                                        <option value="10">10</option>
                                        <option value="15">15</option>
                                        <option value="50">50</option>
                                        <option value="All">{t('registers.all')}</option>
                                    </select>
                                </div>
                            </div>
                            <button
                                className="btn btn-secondary"
                                onClick={handleNextPage}
                                disabled={currentPage === Math.ceil(data.length / itemsPerPage)}
                            >
                                {t('registers.next')} <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>

                    {showModal && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    {showResolved === 'Connections' && (
                                        <h6>{t('registers.newConnection')}</h6>
                                    )}
                                    {showResolved === 'Reservations' && (
                                        <h6>{t('registers.newReservation')}</h6>
                                    )}
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.vessel')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalVesselNew}
                                            onChange={(e) => setModalVesselNew(e.target.value)}
                                            onFocus={handleSelectFocusVessel}
                                            onBlur={handleSelectBlurVessel}
                                            disabled={role === "costumer" && filteredDataVessel.length === 1}
                                        >
                                            {role !== "costumer" && (
                                                <option value="">{`${t('registers.selectA1')} ${t('registers.vessel').toLowerCase()}`} ...</option>
                                            )}

                                            {filteredDataVessel.map((item) => (
                                                <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                    {item.Tembarcacion}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenVessel ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.gridPoint')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalGridPointNew}
                                            onChange={(e) => setModalGridPointNew(e.target.value)}
                                            onFocus={handleSelectFocusGridPoint}
                                            onBlur={handleSelectBlurGridPoint}
                                        >
                                            <option value="">{`${t('registers.selectA2')} ${t('registers.gridPoint').toLowerCase()}`} ...</option>
                                            {filteredDataGridPoint
                                                .map((item) => (
                                                    <option key={item.Nmonaguillo} value={String(item.Tmonaguillo)}>
                                                        {item.Tmonaguillo}
                                                    </option>
                                                ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenGridPoint ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                {showResolved === 'Connections' && (
                                    <div className='formInput'>
                                        <h6><span>*</span> {t('registers.startDate')} (UTC)</h6>
                                        <DatePicker
                                            selected={modalDateInicialNew ? new Date(modalDateInicialNew) : null}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    setModalDateInicialNew(date.toISOString());
                                                    setModalDateFinalNew(date.toISOString());
                                                } else {
                                                    setModalDateInicialNew("");
                                                    setModalDateFinalNew("");
                                                }
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm:ss"
                                            timeIntervals={1}
                                            dateFormat="dd/MM/yyyy, HH:mm:ss"
                                            timeCaption="Time"
                                            className="form-control"
                                            maxDate={new Date()}
                                            placeholderText={`${t('registers.selectA1')} ${t('registers.startDate').toLowerCase()} (UTC)`}
                                        />
                                    </div>
                                )}
                                {showResolved === 'Connections' && (
                                    <>
                                        <div className='formInput'>
                                            <h6><span>*</span> {t('registers.finalDate')} (UTC)</h6>
                                            <DatePicker
                                                selected={modalDateFinalNew ? new Date(modalDateFinalNew) : null}
                                                onChange={(date: Date | null) => {
                                                    if (date) {
                                                        if (date.toISOString() >= modalDateInicialNew) {
                                                            setModalDateFinalNew(date.toISOString());
                                                        } else {
                                                            toast.error(`${t('registers.theEndDateCannotBeLessThanTheStartDate')}`);
                                                            setModalDateFinalNew(modalDateInicialNew);
                                                        }
                                                    } else {
                                                        setModalDateFinalNew("");
                                                    }
                                                }}
                                                showTimeSelect
                                                timeFormat="HH:mm:ss"
                                                timeIntervals={1}
                                                dateFormat="dd/MM/yyyy, HH:mm:ss"
                                                timeCaption="Time"
                                                className="form-control"
                                                maxDate={new Date()}
                                                disabled={!modalDateInicialNew}
                                                placeholderText={`${t('registers.selectA1')} ${t('registers.finalDate').toLowerCase()} (UTC)`}
                                            />
                                        </div>
                                        <div className='formInput'>
                                            <h6><span>*</span> {t('registers.initialConsumption')}</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalInitialConsumptionNew}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                                                        setModalInitialConsumptionNew(value);
                                                    }
                                                }}
                                                placeholder={`${t('registers.enterThe')} ${t('registers.initialConsumption').toLowerCase()}`}
                                            />
                                        </div>

                                        <div className='formInput'>
                                            <h6><span>*</span> {t('registers.finalConsumption')}</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalFinalConsumptionNew}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                                                        setModalFinalConsumptionNew(value);
                                                    }
                                                }}
                                                disabled={!modalInitialConsumptionNew}
                                                placeholder={`${t('registers.enterThe')} ${t('registers.finalConsumption').toLowerCase()}`}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className='formBtn' style={role === 'admin' ? { justifyContent: 'space-between' } : { justifyContent: 'end' }}>
                                    {/* {role === 'admin' && (
                                        <div className='buttons' >
                                            <button className={`btn ${showResolved === "Reservations" && 'active'}`} onClick={() => {
                                                setShowResolved('Reservations');
                                                setModalVesselNew("");
                                                setModalGridPointNew("");
                                                setModalDateInicialNew("");
                                                setModalDateFinalNew("");
                                                setModalInitialConsumptionNew("");
                                                setModalFinalConsumptionNew("");
                                                setModalStatus("");
                                                setModalDateInicialNew(minDate);
                                                setModalDateFinalNew(minDate);
                                            }}>
                                                {t('registers.reservations')}
                                            </button>
                                            <button className={`btn ${showResolved === "Connections" && 'active'}`} onClick={() => {
                                                setShowResolved('Connections');
                                                setModalVesselNew("");
                                                setModalGridPointNew("");
                                                setModalDateInicialNew("");
                                                setModalDateFinalNew("");
                                                setModalInitialConsumptionNew("");
                                                setModalFinalConsumptionNew("");
                                                setModalStatus("");
                                                setModalDateInicialNew(minDate);
                                                setModalDateFinalNew(minDate);
                                            }}>
                                                {t('registers.connections')}
                                            </button>
                                        </div>
                                    )} */}
                                    {showResolved === 'Connections' && (
                                        <button className="btn btn-dark" onClick={handleCreateConnection}>
                                            {t('registers.createConnection')}
                                        </button>
                                    )}
                                    {showResolved === 'Reservations' && (
                                        <button className="btn btn-dark" onClick={handleCreateReservation}>
                                            {t('registers.createReservation')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showModalEdit && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    {showResolved === 'Connections' && (
                                        <h6>{t('registers.editConnection')}</h6>
                                    )}
                                    {showResolved === 'Reservations' && (
                                        <h6>{t('registers.editReservation')}</h6>
                                    )}
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                <div className='formInput'>
                                    <h6>ID</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalID}
                                            onChange={(e) => {
                                                setModalID(e.target.value);
                                                const selectedItem = filteredDataConnection.find((data) => data.Nconexion === parseFloat(e.target.value));
                                                const vesselMatch = dataVessel.find(data => data.Nembarcacion === selectedItem?.Nembarcacion);
                                                const gridPointMatch = dataGridPoint.find(data => data.Nmonaguillo === selectedItem?.Nmonaguillo);
                                                const statusMatch = dataStatusConnection.find(data => data.Nestado === selectedItem?.Status);
                                                setModalVessel(vesselMatch ? vesselMatch.Tembarcacion.toString() : "");
                                                setModalGridPoint(gridPointMatch ? gridPointMatch.Tmonaguillo.toString() : "");
                                                setModalDateInicial(selectedItem ? selectedItem.Finicio : "");
                                                setModalDateFinal(selectedItem ? selectedItem.Ffinal : "");
                                                setModalStatus(statusMatch ? statusMatch.Testado.toString() : "");
                                                setModalInitialConsumption(selectedItem ? selectedItem.Cinicio.toString() : "");
                                                setModalFinalConsumption(selectedItem && selectedItem.Cfinal !== null ? selectedItem.Cfinal.toString() : "");
                                            }}
                                            onFocus={handleSelectFocusID}
                                            onBlur={handleSelectBlurID}
                                            disabled={showResolved === "Reservations"}
                                        >
                                            <option value="">{`${t('registers.selectA2')} ID`} ...</option>
                                            {showResolved === "Connections" && filteredDataConnectionID
                                                .map((item) => (
                                                    <option key={item.Nconexion} value={String(item.Nconexion)}>
                                                        {item.Nconexion}
                                                    </option>
                                                ))}
                                            {showResolved === "Reservations" && filteredDataID
                                                .map((item) => (
                                                    <option key={item.ID} value={String(item.ID)}>
                                                        {item.ID}
                                                    </option>
                                                ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenID ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.vessel')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalVessel}
                                            onChange={(e) => setModalVessel(e.target.value)}
                                            onFocus={handleSelectFocusVessel}
                                            onBlur={handleSelectBlurVessel}
                                            disabled={!modalID || (role === "costumer" && filteredDataVessel.length === 1)}
                                        >
                                            {role !== "costumer" && (
                                                <option value="">{`${t('registers.selectA1')} ${t('registers.vessel').toLowerCase()}`} ...</option>
                                            )}

                                            {filteredDataVessel.map((item) => (
                                                <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                    {item.Tembarcacion}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenVessel ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.gridPoint')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalGridPoint}
                                            onChange={(e) => setModalGridPoint(e.target.value)}
                                            onFocus={handleSelectFocusGridPoint}
                                            onBlur={handleSelectBlurGridPoint}
                                            disabled={!modalID || !ability.can('update', 'AdminPage')}
                                        >
                                            <option value="">{`${t('registers.selectA2')} ${t('registers.gridPoint').toLowerCase()}`} ...</option>
                                            {filteredDataGridPoint
                                                .map((item) => (
                                                    <option key={item.Nmonaguillo} value={String(item.Tmonaguillo)}>
                                                        {item.Tmonaguillo}
                                                    </option>
                                                ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenGridPoint ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.startDate')} (UTC)</h6>
                                    <DatePicker
                                        selected={modalDateInicial ? new Date(modalDateInicial) : null}
                                        onChange={(date: Date | null) => {
                                            if (date) {
                                                if (date.toISOString() >= minDate) {
                                                    setModalDateInicial(date.toISOString());
                                                    setModalDateFinal(date.toISOString());
                                                } else {
                                                    toast.error(`${t('registers.theDateMustBeGreaterThanOrEqualToTheCurrentDateInUTC')}`);
                                                    setModalDateInicial(minDate);
                                                }
                                            } else {
                                                setModalDateInicial("");
                                                setModalDateFinal("");
                                            }
                                        }}
                                        maxDate={showResolved === 'Connections' ? new Date() : undefined}
                                        showTimeSelect
                                        timeFormat="HH:mm:ss"
                                        timeIntervals={1}
                                        dateFormat="dd/MM/yyyy, HH:mm:ss"
                                        timeCaption="Time"
                                        className="form-control"
                                        disabled={!modalID || !ability.can('update', 'AdminPage')}
                                        placeholderText={`${t('registers.selectA1')} ${t('registers.startDate').toLowerCase()} (UTC)`}
                                    />
                                </div>
                                {showResolved === 'Connections' && (
                                    <>
                                        <div className='formInput'>
                                            <h6><span>*</span> {t('registers.finalDate')} (UTC)</h6>
                                            <DatePicker
                                                selected={modalDateInicial ? new Date(modalDateInicial) : null}
                                                onChange={(date: Date | null) => {
                                                    if (date) {
                                                        if (date.toISOString() >= modalDateInicial) {
                                                            setModalDateFinal(date.toISOString());
                                                        } else {
                                                            toast.error(`${t('registers.theDateMustBeGreaterThanOrEqualToTheCurrentDateInUTC')}`);
                                                            setModalDateFinal(modalDateInicial);
                                                        }
                                                    } else {
                                                        setModalDateFinal("");
                                                    }
                                                }}
                                                maxDate={new Date()}
                                                showTimeSelect
                                                timeFormat="HH:mm:ss"
                                                timeIntervals={1}
                                                dateFormat="dd/MM/yyyy, HH:mm:ss"
                                                timeCaption="Time"
                                                className="form-control"
                                                disabled={!modalDateInicial}
                                                placeholderText={`${t('registers.selectA1')} ${t('registers.finalDate').toLowerCase()} (UTC)`}
                                            />
                                        </div>
                                        <div className='formInput'>
                                            <h6><span>*</span> {t('registers.initialConsumption')}</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalInitialConsumption}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*\.?\d*$/.test(value)) {
                                                        setModalInitialConsumption(value);
                                                    }
                                                }}
                                                disabled={!modalID}
                                                placeholder={`${t('registers.enterThe')} ${t('registers.initialConsumption').toLowerCase()}`}
                                            />
                                        </div>

                                        <div className='formInput'>
                                            <h6><span>*</span> {t('registers.finalConsumption')}</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalFinalConsumption}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*\.?\d*$/.test(value)) {
                                                        setModalFinalConsumption(value);
                                                    }
                                                }}
                                                disabled={!modalID}
                                                placeholder={`${t('registers.enterThe')} ${t('registers.finalConsumption').toLowerCase()}`}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.status')}</h6>
                                    <div className="select-wrapper">
                                        {showResolved === 'Connections' && (
                                            <select
                                                className="form-control"
                                                value={modalStatus}
                                                onChange={(e) => setModalStatus(e.target.value)}
                                                onFocus={handleSelectFocusStatus}
                                                onBlur={handleSelectBlurStatus}
                                                disabled={!modalID}
                                            >
                                                <option value="">{`${t('registers.selectA2')} ${t('registers.status').toLowerCase()}`} ...</option>
                                                {dataStatusConnection.map((status) => (
                                                    <option key={status.Nestado} value={status.Testado}>
                                                        {status.Testado}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {showResolved === 'Reservations' && (
                                            <select
                                                className="form-control"
                                                value={modalStatus}
                                                onChange={(e) => setModalStatus(e.target.value)}
                                                onFocus={handleSelectFocusStatus}
                                                onBlur={handleSelectBlurStatus}
                                                disabled={!ability.can('update', 'AdminPage')}
                                            >
                                                <option value="">Select a status...</option>
                                                {dataStatusReservation.map((status) => (
                                                    <option key={status.Nestado} value={status.Testado}>
                                                        {status.Testado}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        <span className="icon">
                                            {isSelectOpenStatus ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formBtn' style={role === 'admin' ? { justifyContent: 'space-between' } : { justifyContent: 'end' }}>
                                    {/* {role === 'admin' && (
                                        <div className='buttons' >
                                            <button className={`btn ${showResolved === "Reservations" && 'active'}`} onClick={() => {
                                                setShowResolved('Reservations');
                                                setModalVessel("");
                                                setModalGridPoint("");
                                                setModalDateInicial("");
                                                setModalDateFinal("");
                                                setModalInitialConsumption("");
                                                setModalFinalConsumption("");
                                                setModalStatus("");

                                                setShowModalEdit(true);
                                                setModalID(modalIdTemp);
                                                const selectedItem = filteredData.find((data) => data.ID === modalIdTemp);
                                                const statusMatch = dataStatusReservation.find(data => data.Nestado === modalStatusTemp);
                                                setModalVessel(selectedItem ? selectedItem.Vessel : "");
                                                setModalGridPoint(selectedItem ? selectedItem.GridPoint : "");
                                                setModalDateInicial(selectedItem ? selectedItem.StartTime : "");
                                                setModalStatus(statusMatch ? statusMatch.Testado.toString() : "");
                                            }}>
                                                {t('registers.reservations')}
                                            </button>
                                            <button className={`btn ${showResolved === "Connections" && 'active'}`} onClick={() => {
                                                setShowResolved('Connections');
                                                setModalVessel("");
                                                setModalGridPoint("");
                                                setModalDateInicial("");
                                                setModalDateFinal("");
                                                setModalInitialConsumption("");
                                                setModalFinalConsumption("");
                                                setModalStatus("");
                                                setModalID("");
                                            }}>
                                                {t('registers.connections')}
                                            </button>
                                        </div>
                                    )} */}
                                    {showResolved === 'Connections' && (
                                        <button className="btn btn-dark" onClick={handleUpdateConnection}>
                                            {t('registers.updateConnection')}
                                        </button>
                                    )}
                                    {showResolved === 'Reservations' && (
                                        <button className="btn btn-dark" onClick={handleUpdateReservation}>
                                            {t('registers.updateReservation')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showModalDelete && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>{t('registers.deleteReservation')}</h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                <div className='formInput'>
                                    <h6>ID</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalID}
                                            onChange={(e) => {
                                                setModalID(e.target.value);
                                                const selectedItem = filteredDataConnection.find((data) => data.Nconexion === parseFloat(e.target.value));
                                                const vesselMatch = dataVessel.find(data => data.Nembarcacion === selectedItem?.Nembarcacion);
                                                const gridPointMatch = dataGridPoint.find(data => data.Nmonaguillo === selectedItem?.Nmonaguillo);
                                                const statusMatch = dataStatusConnection.find(data => data.Nestado === selectedItem?.Status);
                                                setModalVessel(vesselMatch ? vesselMatch.Tembarcacion.toString() : "");
                                                setModalGridPoint(gridPointMatch ? gridPointMatch.Tmonaguillo.toString() : "");
                                                setModalDateInicial(selectedItem ? selectedItem.Finicio : "");
                                                setModalDateFinal(selectedItem ? selectedItem.Ffinal : "");
                                                setModalStatus(statusMatch ? statusMatch.Testado.toString() : "");
                                                setModalInitialConsumption(selectedItem ? selectedItem.Cinicio.toString() : "");
                                                setModalFinalConsumption(selectedItem && selectedItem.Cfinal !== null ? selectedItem.Cfinal.toString() : "");
                                            }}
                                            onFocus={handleSelectFocusID}
                                            onBlur={handleSelectBlurID}
                                            disabled={showResolved === "Reservations"}
                                        >
                                            <option value="">Select a ID...</option>
                                            {filteredDataID
                                                .map((item) => (
                                                    <option key={item.ID} value={String(item.ID)}>
                                                        {item.ID}
                                                    </option>
                                                ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenID ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.vessel')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalVessel}
                                            onChange={(e) => setModalVessel(e.target.value)}
                                            onFocus={handleSelectFocusVessel}
                                            onBlur={handleSelectBlurVessel}
                                            disabled
                                        >
                                            <option value="">Select a vessel...</option>
                                            {filteredDataVessel
                                                .map((item) => (
                                                    <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                        {item.Tembarcacion}
                                                    </option>
                                                ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenVessel ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.gridPoint')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalGridPoint}
                                            onChange={(e) => setModalGridPoint(e.target.value)}
                                            onFocus={handleSelectFocusGridPoint}
                                            onBlur={handleSelectBlurGridPoint}
                                            disabled
                                        >
                                            <option value="">Select a grip point...</option>
                                            {filteredDataGridPoint
                                                .map((item) => (
                                                    <option key={item.Nmonaguillo} value={String(item.Tmonaguillo)}>
                                                        {item.Tmonaguillo}
                                                    </option>
                                                ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenGridPoint ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.startDate')} (UTC)</h6>
                                    <DatePicker
                                        selected={modalDateInicial ? new Date(modalDateInicial) : null}
                                        onChange={(date: Date | null) => {
                                            if (date) {
                                                if (date.toISOString() >= minDate) {
                                                    setModalDateInicial(date.toISOString());
                                                    setModalDateFinal(date.toISOString());
                                                } else {
                                                    toast.error(`${t('registers.theDateMustBeGreaterThanOrEqualToTheCurrentDateInUTC')}`);
                                                    setModalDateInicial(minDate);
                                                }
                                            } else {
                                                setModalDateInicial("");
                                                setModalDateFinal("");
                                            }
                                        }}
                                        maxDate={undefined}
                                        showTimeSelect
                                        timeFormat="HH:mm:ss"
                                        timeIntervals={1}
                                        dateFormat="dd/MM/yyyy, HH:mm:ss"
                                        timeCaption="Time"
                                        className="form-control"
                                        disabled
                                        placeholderText="Select start date (UTC)"
                                    />
                                </div>
                                <div className='formInput'>
                                    <h6><span>*</span> {t('registers.status')}</h6>
                                    <div className="select-wrapper">
                                        <select
                                            className="form-control"
                                            value={modalStatus}
                                            onChange={(e) => setModalStatus(e.target.value)}
                                            onFocus={handleSelectFocusStatus}
                                            onBlur={handleSelectBlurStatus}
                                            disabled
                                        >
                                            <option value="">Select a status...</option>
                                            {dataStatusReservation.map((status) => (
                                                <option key={status.Nestado} value={status.Testado}>
                                                    {status.Testado}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="icon">
                                            {isSelectOpenStatus ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='formBtn'>
                                    <button className="btn btn-dark" onClick={handleDeleteReservation}>
                                        {t('registers.deleteReservation')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Registers;